<template>
  <div class="navbar-tooltip-overlay" v-if="featureId" :style="adjustedPositionStyle" :class="{ 'position-adjusted': needsPositionAdjustment }">
    <div class="navbar-tooltip-content" :class="{ 'coming-soon': isComingSoon }" ref="tooltipContent">
      <div class="navbar-tooltip-header">
        <h3>{{ name }}</h3>
        <div v-if="isComingSoon" class="coming-soon-badge">Coming Soon!</div>
      </div>

      <!-- Image container -->
      <div v-if="hasImage" class="navbar-tooltip-image-wrapper">
        <!-- Loading spinner -->
        <div v-if="!imageLoaded && !imageError" class="image-loading">
          <q-spinner color="primary" size="32px" />
        </div>

        <!-- Image -->
        <img
          :src="getImageUrl()"
          :alt="name"
          class="navbar-tooltip-image"
          :class="{ 'image-loaded': imageLoaded }"
          @load="onImageLoad"
          @error="onImageError"
          style="display: none"
          ref="tooltipImage"
        />

        <!-- Error state -->
        <div v-if="imageError" class="image-error">
          <q-icon name="image_not_supported" size="32px" color="grey-5" />
          <span class="error-text">Failed to load image</span>
        </div>
      </div>

      <p class="navbar-tooltip-description">{{ description }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NavbarTooltip',
  props: {
    featureId: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    isComingSoon: {
      type: Boolean,
      default: false
    },
    positionStyle: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      imageLoaded: false,
      imageError: false,
      tooltipHeight: 300 // Estimated tooltip height
    };
  },
  computed: {
    hasImage() {
      return this.featureId && this.getImageUrls()[this.featureId];
    },
    adjustedPositionStyle() {
      if (!this.positionStyle) return {};

      const style = { ...this.positionStyle };

      // Check if tooltip would go off bottom of screen
      if (this.needsPositionAdjustment) {
        // Move tooltip above the trigger element instead of below
        const currentTop = parseInt(style.top) || 0;
        style.top = `${currentTop - this.tooltipHeight - 20}px`; // 20px gap
      }

      return style;
    },
    needsPositionAdjustment() {
      if (!this.positionStyle || !this.positionStyle.top) return false;

      const currentTop = parseInt(this.positionStyle.top) || 0;
      const windowHeight = window.innerHeight;
      const bottomSpace = windowHeight - currentTop;

      // If there's less than tooltip height + buffer space, adjust position
      return bottomSpace < this.tooltipHeight + 50;
    }
  },
  methods: {
    getImageUrls() {
      return {
        templates: require('@/assets/gif/Template.png'),
        collections: require('@/assets/gif/Collections.png'),
        brandKits: require('@/assets/gif/Brandkit.png'),
        analytics: require('@/assets/gif/Analytics.png'),
        smartWorkflow: require('@/assets/gif/Flow Chart.png'),
        publishToPlatform: require('@/assets/gif/Social Media Publishing.png'),
        cloudSync: require('@/assets/gif/Cloud Sync.png')
      };
    },

    getImageUrl() {
      return this.getImageUrls()[this.featureId] || '';
    },

    onImageLoad() {
      this.imageLoaded = true;
      this.imageError = false;

      // Show the image with fade-in effect
      if (this.$refs.tooltipImage) {
        this.$refs.tooltipImage.style.display = 'block';
        this.$refs.tooltipImage.style.opacity = '0';

        // Trigger fade-in
        this.$nextTick(() => {
          this.$refs.tooltipImage.style.opacity = '1';
        });
      }
    },

    onImageError() {
      this.imageLoaded = false;
      this.imageError = true;

      // Hide the image
      if (this.$refs.tooltipImage) {
        this.$refs.tooltipImage.style.display = 'none';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.navbar-tooltip-overlay {
  position: fixed;
  z-index: 10005;
  pointer-events: none;
  width: auto;
  height: auto;

  // Ensure tooltip stays within viewport
  &.position-adjusted {
    transform: translateY(60%) !important;
  }
}

.navbar-tooltip-content {
  background: white;
  border-radius: 12px;
  padding: 20px;
  min-width: 360px;
  max-width: 420px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 12px;
  backdrop-filter: blur(10px);

  &.coming-soon {
    border: 2px dashed #ccc;
    background: #f9f9f9;
  }
}

.navbar-tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;

  h3 {
    @include mona-sans-font($size: 20px, $bolded: true, $color: var(--main-text-color));
    margin: 0;
  }
}

.coming-soon-badge {
  background: #fecb2f;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  margin-left: 12px;
  color: var(--main-text-color);
}

.navbar-tooltip-description {
  @include inter-font($size: 14px, $bolded: false, $color: var(--secondary-text-color));
  margin: 0;
  line-height: 1.4;
  text-align: left;
}

.navbar-tooltip-image-wrapper {
  position: relative;
  margin: 4px 0;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f9f9f9;
}

.navbar-tooltip-image {
  border: 1px solid var(--main-card-color);
  width: 100%;
  height: 100%;
  object-fit: fill;
  border-radius: 8px;
  transition: opacity 0.3s ease;
}

.image-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
}

.image-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;

  .error-text {
    @include inter-font($size: 14px, $bolded: false, $color: var(--secondary-text-color));
    margin-top: 4px;
  }
}
</style>
